import { FC, ReactNode, useCallback, useState } from 'react';
import { InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FieldError, useFormContext, useWatch } from 'react-hook-form-mui';
import { EyeOffIcon, EyeOnIcon } from 'assets';
import * as S from './styled';

interface IInputProps {
  name: string;
  value?: string;
  label: string;
  subLabel?: string;
  type?: 'email' | 'password' | 'text';
  rules?: any;
  parseError?: (error: FieldError) => ReactNode;
  placeholder?: string;
  marginbottom?: number | undefined;
  onChange?: any;
  helperText?: string;
  multiline?: boolean; // ✅ Добавляем поддержку `TextArea`
  rows?: number; // ✅ Количество строк
  startAdornment?: any;
  clearable?: boolean; // ✅ Показывать крестик очистки, когда в поле есть значение
  onClear?: () => void;
}

const Input: FC<IInputProps> = ({
  name,
  value,
  label,
  subLabel = '',
  type = 'text',
  rules,
  parseError,
  placeholder,
  marginbottom,
  onChange,
  helperText = '',
  multiline = false, // ✅ По умолчанию `false`
  rows = 4, // ✅ Значение по умолчанию
  startAdornment,
  clearable = false,
  onClear,
}) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { setValue } = useFormContext();
  const fieldValue = useWatch({ name });

  const handleClickShowPassword = () => {
    return setShowPassword((show) => {
      return !show;
    });
  };

  const getInputType = useCallback(
    (inputType: string | undefined): string => {
      if (!inputType || (inputType === 'password' && showPassword)) return 'text';
      if (inputType === 'password' && !showPassword) return 'password';
      return inputType;
    },
    [showPassword]
  );

  return (
    <S.Input
      name={name}
      value={value || ''}
      label={
        subLabel ? (
          <>
            <p>{label}</p>
            <p className="subLabel">{subLabel}</p>
          </>
        ) : (
          label
        )
      }
      type={multiline ? undefined : getInputType(type)} // ✅ Отключаем `type` если `multiline`
      placeholder={placeholder}
      variant="standard"
      fullWidth
      rules={rules || {}}
      onChange={onChange}
      multiline={multiline} // ✅ Передаем `multiline`
      rows={multiline ? rows : undefined} // ✅ Передаем `rows`, если `multiline=true`
      slotProps={{
        inputLabel: {
          shrink: true,
        },
        input: {
          endAdornment:
            type === 'password' && !multiline ? ( // ✅ Убираем `endAdornment` для `TextArea`
              <InputAdornment
                position="end"
                onClick={handleClickShowPassword}
                style={{ cursor: 'pointer' }}
              >
                {showPassword ? <EyeOnIcon /> : <EyeOffIcon />}
              </InputAdornment>
            ) : clearable && fieldValue ? ( // ✅ Крестик очистки при наличии значения
              <InputAdornment
                position="end"
                onClick={() => {
                  setValue(name, '');
                  onClear?.();
                }}
                style={{ cursor: 'pointer' }}
              >
                <CloseIcon fontSize="small" />
              </InputAdornment>
            ) : null,
          startAdornment: startAdornment || null,
        },
      }}
      parseError={parseError}
      marginbottom={marginbottom ?? 24}
      subLabel={subLabel}
      helperText={helperText}
    />
  );
};

export default Input;
